import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CallProvider } from "@/context/CallContext";
import { RTCProvider } from "@/context/RTCContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { ProductivityProvider } from "@/context/ProductivityContext";
import { FloatingToolsProvider } from "@/context/FloatingToolsContext";
import { Toaster } from "@/components/ui/sonner";
import { OwnerLayout } from "@/components/design-system";

import Landing from "@/pages/Landing";
import { Navigate as _Navigate } from "react-router-dom";
import { isTeamDevice as _isTeamDevice } from "@/lib/deviceRole";
import { useAuth as _useAuth } from "@/context/AuthContext";

/** Guards the public marketing landing page.
 *
 * Product rule: team members (agents / supervisors) may NEVER see the marketing
 * landing page. Two independent signals gate access — either one blocks:
 *
 *   1. **Live auth state** — the currently authenticated user's server-side
 *      role. If a team member is signed in and edits the URL to `/`, we ignore
 *      any missing device-role marker and send them into their app anyway.
 *   2. **Device-role marker** — `hbf.device_role` in localStorage, written on
 *      every successful login. Covers the logged-out case: if this device has
 *      ever hosted an agent's session, a fresh visit to `/` still goes to
 *      `/login` (the workspace entry), not the marketing surface.
 *
 * If both signals miss (public visitor, admin/owner user, brand-new device),
 * we render the Landing page normally.
 */
function LandingGate() {
  const auth = _useAuth();
  const user = auth?.user;
  const role = (user && typeof user === "object") ? user.role : null;

  // (1) Server-side truth wins. Logged-in team member → their app.
  if (role === "agent" || role === "supervisor") {
    return <_Navigate to="/app" replace />;
  }

  // (2) Device previously used by an agent, but currently logged out (or a
  //     different, non-agent user). Team devices never see the landing page.
  if (_isTeamDevice()) return <_Navigate to="/login" replace />;

  return <Landing />;
}
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MfaVerify from "@/pages/MfaVerify";
import Register from "@/pages/Register";
import EnterpriseInquiry from "@/pages/EnterpriseInquiry";
import BusinessFunnel from "@/pages/BusinessFunnel";
import WelcomeWizard from "@/pages/WelcomeWizard";
import SeatLockedGate from "@/components/SeatLockedGate";
// iter137 Batch B · route-level code splitting for heavy admin/owner surfaces.
const OwnerConsole              = lazy(() => import("@/pages/OwnerConsole"));
const OrgDeepView               = lazy(() => import("@/pages/OrgDeepView"));
const OwnerDigest               = lazy(() => import("@/pages/OwnerDigest"));
const OwnerBilling              = lazy(() => import("@/pages/OwnerBilling"));
const OwnerBillingConfig        = lazy(() => import("@/pages/OwnerBillingConfig"));
const OwnerStorage              = lazy(() => import("@/pages/OwnerStorage"));
const OwnerReadiness            = lazy(() => import("@/pages/OwnerReadiness"));
const OwnerGrowth               = lazy(() => import("@/pages/OwnerGrowth"));
const OwnerSecurity             = lazy(() => import("@/pages/OwnerSecurity"));
const OwnerSettings             = lazy(() => import("@/pages/OwnerSettings"));
const OwnerPrivilegedEmails     = lazy(() => import("@/pages/OwnerPrivilegedEmails"));
const OwnerWaitlistDesktop      = lazy(() => import("@/pages/OwnerWaitlistDesktop"));
const OwnerOrgs                 = lazy(() => import("@/pages/OwnerOrgs"));
const OwnerRefunds              = lazy(() => import("@/pages/OwnerRefunds"));
const OwnerAttention            = lazy(() => import("@/pages/OwnerAttention"));
const OwnerRtcCaptures          = lazy(() => import("@/pages/OwnerRtcCaptures"));
import { DeveloperModeProvider } from "@/context/DeveloperModeContext";
const OwnerNotifications        = lazy(() => import("@/pages/OwnerNotifications"));
const OwnerDesignGallery        = lazy(() => import("@/pages/OwnerDesignGallery"));
const OwnerDesignOverview       = lazy(() => import("@/pages/design/OwnerDesignOverview"));
const OwnerDesignCompliance     = lazy(() => import("@/pages/design/OwnerDesignCompliance"));
const RefDashboard              = lazy(() => import("@/pages/design/RefDashboard"));
const RefManagement             = lazy(() => import("@/pages/design/RefManagement"));
const RefMonitoring             = lazy(() => import("@/pages/design/RefMonitoring"));
const RefConfiguration          = lazy(() => import("@/pages/design/RefConfiguration"));
const RefWizard                 = lazy(() => import("@/pages/design/RefWizard"));
const RefDetail                 = lazy(() => import("@/pages/design/RefDetail"));
const OwnerProduction           = lazy(() => import("@/pages/OwnerProduction"));
const InternalProviders         = lazy(() => import("@/pages/InternalProviders"));
const LaunchWizard              = lazy(() => import("@/pages/LaunchWizard"));
const AuditCenter               = lazy(() => import("@/pages/AuditCenter"));
const SOCCenter                 = lazy(() => import("@/pages/SOCCenter"));
const OwnerIdentity             = lazy(() => import("@/pages/OwnerIdentity"));
const IdentityVerify            = lazy(() => import("@/pages/IdentityVerify"));
const IdentityComplete          = lazy(() => import("@/pages/IdentityComplete"));
const InternalStaff             = lazy(() => import("@/pages/InternalStaff"));
const Analytics                 = lazy(() => import("@/pages/Analytics"));
const Sessions                  = lazy(() => import("@/pages/Sessions"));
import StaffLogin from "@/pages/StaffLogin";
import StaffChangePassword from "@/pages/StaffChangePassword";
import StaffAcceptInvite from "@/pages/StaffAcceptInvite";
import AppLayout from "@/components/AppLayout";
import ForcePasswordChangeGate from "@/components/ForcePasswordChangeGate";
import SupportChatWidget from "@/components/SupportChatWidget";
import MeetingRoomModal from "@/components/MeetingRoomModal";
import IncomingMeetingCallToast from "@/components/IncomingMeetingCallToast";
import CallDock from "@/components/CallDock";
import ActiveCallBanner from "@/components/ActiveCallBanner";
import { CommandPaletteProvider } from "@/components/CommandPalette";
import { Terms, Privacy, AcceptableUse, Compliance, Cookies } from "@/pages/LegalPages";
import Download from "@/pages/Download";
import Dashboard from "@/pages/Dashboard";
import DialerPage from "@/pages/DialerPage";
import Contacts from "@/pages/Contacts";
import CallLogs from "@/pages/CallLogs";
import Recordings from "@/pages/Recordings";
import Messages from "@/pages/Messages";
import Meetings from "@/pages/Meetings";
import Numbers from "@/pages/Numbers";
import Team from "@/pages/Team";
import Leaderboard from "@/pages/Leaderboard";
import Coaching from "@/pages/Coaching";
import Billing from "@/pages/Billing";
import IVRBuilder from "@/pages/IVRBuilder";
import Settings from "@/pages/Settings";import Developers from "@/pages/Developers";
import Onboarding from "@/pages/Onboarding";
const HxShowcase = process.env.NODE_ENV === "development" ? require("@/pages/HxShowcase").default : null;

import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="hx-splash" data-testid="auth-loading">
        <div className="hx-splash__inner">
          <img src="/brand/hbf-logo-3d-wm.png" alt="Hello by Flow" draggable={false} />
          <div className="hx-splash__bar" aria-hidden="true" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  return children;
}

function OwnerProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/app" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function BusinessOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.organization?.type === "individual") return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  // moved above; legacy stub
  return children;
}

/**
 * StaffProtected — grants access if EITHER:
 *   • customer cookie says user.role === 'owner' (Platform Owner path), OR
 *   • dedicated internal_access_token cookie is valid (invited staff path).
 * The backend is the source of truth; this component just hits /internal/me
 * to make the routing decision on first render.
 */
function StaffProtected({ children }) {
  const { user, loading } = useAuth();
  const [status, setStatus] = React.useState("checking"); // checking | ok | forbidden
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import("@/lib/api");
        await mod.default.get("/internal/me");
        if (!cancelled) setStatus("ok");
      } catch {
        if (!cancelled) setStatus("forbidden");
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);
  if (loading || status === "checking") {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="font-mono text-[12px] tracking-widest text-[var(--text-muted)]">LOADING CONSOLE…</div></div>;
  }
  if (status === "forbidden") return <Navigate to="/staff/login" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DeveloperModeProvider>
        <CallProvider>
          <NotificationProvider>
            <RTCProvider>
              <OnboardingProvider>
              <ProductivityProvider>
              <FloatingToolsProvider>
              <CommandPaletteProvider>
              <ForcePasswordChangeGate />
              <Suspense fallback={<div style={{padding:"32px", fontFamily:"var(--hx-mono, monospace)", color:"var(--text-muted, #666)"}}>Loading…</div>}>
              <Routes>
              <Route path="/" element={<LandingGate />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/mfa/verify" element={<MfaVerify />} />
              {/* Legacy signup URLs — hard-redirect into the gated funnel so
                  the KYC-before-payment gate cannot be bypassed by inbound
                  bookmarks / old share links. */}
              <Route path="/signup" element={<Navigate to="/start/business" replace />} />
              <Route path="/register" element={<Navigate to="/start/business" replace />} />
              <Route path="/enterprise-inquiry" element={<EnterpriseInquiry />} />
              <Route path="/start/business" element={<BusinessFunnel />} />
              <Route path="/start/business/*" element={<BusinessFunnel />} />
              <Route path="/welcome" element={<WelcomeWizard />} />
              <Route element={<OwnerLayout />}>
              <Route path="/owner" element={<OwnerProtected><OwnerConsole /></OwnerProtected>} />
              <Route path="/owner/orgs/:orgId" element={<OwnerProtected><OrgDeepView /></OwnerProtected>} />
              <Route path="/owner/digest" element={<OwnerProtected><OwnerDigest /></OwnerProtected>} />
              <Route path="/owner/billing" element={<OwnerProtected><OwnerBilling /></OwnerProtected>} />
              <Route path="/owner/billing/config" element={<OwnerProtected><OwnerBillingConfig /></OwnerProtected>} />
              <Route path="/owner/storage" element={<OwnerProtected><OwnerStorage /></OwnerProtected>} />
              <Route path="/owner/readiness" element={<OwnerProtected><OwnerReadiness /></OwnerProtected>} />
              <Route path="/owner/growth" element={<OwnerProtected><OwnerGrowth /></OwnerProtected>} />
              <Route path="/owner/security" element={<StaffProtected><OwnerSecurity /></StaffProtected>} />
              <Route path="/owner/settings" element={<OwnerProtected><OwnerSettings /></OwnerProtected>} />
              <Route path="/owner/privileged-emails" element={<OwnerProtected><OwnerPrivilegedEmails /></OwnerProtected>} />
              <Route path="/owner/waitlist/desktop" element={<OwnerProtected><OwnerWaitlistDesktop /></OwnerProtected>} />
              <Route path="/owner/orgs" element={<OwnerProtected><OwnerOrgs /></OwnerProtected>} />
              <Route path="/owner/billing/refunds" element={<OwnerProtected><OwnerRefunds /></OwnerProtected>} />
              <Route path="/owner/attention" element={<OwnerProtected><OwnerAttention /></OwnerProtected>} />
              <Route path="/owner/rtc-captures" element={<OwnerProtected><OwnerRtcCaptures /></OwnerProtected>} />
              <Route path="/owner/production" element={<OwnerProtected><OwnerProduction /></OwnerProtected>} />
              <Route path="/owner/providers" element={<StaffProtected><InternalProviders /></StaffProtected>} />
              <Route path="/owner/launch" element={<StaffProtected><LaunchWizard /></StaffProtected>} />
              <Route path="/owner/audit" element={<StaffProtected><AuditCenter /></StaffProtected>} />
              <Route path="/owner/soc" element={<StaffProtected><SOCCenter /></StaffProtected>} />
              <Route path="/owner/identity" element={<StaffProtected><OwnerIdentity /></StaffProtected>} />
              <Route path="/owner/staff" element={<StaffProtected><InternalStaff /></StaffProtected>} />
              <Route path="/owner/notifications" element={<OwnerProtected><OwnerNotifications /></OwnerProtected>} />
              <Route path="/owner/design" element={<OwnerProtected><OwnerDesignOverview /></OwnerProtected>} />
              <Route path="/owner/design/gallery" element={<OwnerProtected><OwnerDesignGallery /></OwnerProtected>} />
              <Route path="/owner/design/dashboard" element={<OwnerProtected><RefDashboard /></OwnerProtected>} />
              <Route path="/owner/design/management" element={<OwnerProtected><RefManagement /></OwnerProtected>} />
              <Route path="/owner/design/monitoring" element={<OwnerProtected><RefMonitoring /></OwnerProtected>} />
              <Route path="/owner/design/configuration" element={<OwnerProtected><RefConfiguration /></OwnerProtected>} />
              <Route path="/owner/design/wizard" element={<OwnerProtected><RefWizard /></OwnerProtected>} />
              <Route path="/owner/design/detail" element={<OwnerProtected><RefDetail /></OwnerProtected>} />
              <Route path="/owner/design/compliance" element={<OwnerProtected><OwnerDesignCompliance /></OwnerProtected>} />
              </Route>
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route path="/staff/change-password" element={<StaffChangePassword />} />
              <Route path="/staff/accept-invite/:token" element={<StaffAcceptInvite />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/aup" element={<AcceptableUse />} />
              <Route path="/legal/compliance" element={<Compliance />} />
              <Route path="/legal/cookies" element={<Cookies />} />
              <Route path="/download" element={<Download />} />
              <Route path="/developers" element={<Developers />} />
              <Route path="/developers/*" element={<Developers />} />
              <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
              {HxShowcase && <Route path="/dev/hx" element={<HxShowcase />} />}
              <Route
                path="/app"
                element={<Protected><AppLayout /></Protected>}
              >
                <Route index element={<Dashboard />} />
                <Route path="dialer" element={<SeatLockedGate feature="Dialer"><DialerPage /></SeatLockedGate>} />
                <Route path="contacts" element={<SeatLockedGate feature="Personal Contacts"><Contacts /></SeatLockedGate>} />
                {/* Admin retains access to team call logs, team recordings and team messages — the personal segment is locked inside each page for admins without a seat. */}
                <Route path="calls" element={<CallLogs />} />
                <Route path="recordings" element={<Recordings />} />
                <Route path="sms" element={<Messages />} />
                <Route path="meetings" element={<BusinessOnly><Meetings /></BusinessOnly>} />
                <Route path="numbers" element={<AdminOnly><Numbers /></AdminOnly>} />
                <Route path="team" element={<BusinessOnly><RoleGuard roles={["admin","supervisor"]}><Team /></RoleGuard></BusinessOnly>} />
                <Route path="analytics" element={<RoleGuard roles={["admin","supervisor"]}><Analytics /></RoleGuard>} />
                <Route path="leaderboard" element={<RoleGuard roles={["admin","supervisor"]}><Leaderboard /></RoleGuard>} />
                <Route path="coaching" element={<RoleGuard roles={["admin","supervisor"]}><Coaching /></RoleGuard>} />
                <Route path="settings/sessions" element={<Sessions />} />
                <Route path="billing" element={<RoleGuard roles={["admin"]}><Billing /></RoleGuard>} />
                <Route path="ivr" element={<AdminOnly><IVRBuilder /></AdminOnly>} />
                <Route path="settings" element={<Settings />} />
                <Route path="identity/verify" element={<IdentityVerify />} />
                <Route path="identity/complete" element={<IdentityComplete />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            <SupportChatWidget />
            <MeetingRoomModal />
            <CallDock />
            <IncomingMeetingCallToast />
            <Toaster />
              </CommandPaletteProvider>
              </FloatingToolsProvider>
              </ProductivityProvider>
              </OnboardingProvider>
            </RTCProvider>
          </NotificationProvider>
        </CallProvider>
        </DeveloperModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
